'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Search, Sparkles, ChevronDown, CalendarDays, BookOpen, MessageCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const aiSuggestions = [
  'Satyanarayan Pooja for new home',
  'Ganesh Pooja before business launch',
  'Gruhapravesam ceremony this month',
  'Navratri Devi Pooja at home',
  'Vivaha Samskar — wedding rituals',
  'Pitru Paksha Shraddha ceremony',
];

const trustStats = [
  { value: '12,400+', label: 'Rituals Performed' },
  { value: '840+', label: 'Verified Priests' },
  { value: '4.9★', label: 'Average Rating' },
  { value: '98%', label: 'Devotee Satisfaction' },
];

const quickActions = [
  { icon: CalendarDays, label: 'Book a Ritual', href: '/booking-flow', color: 'text-saffron' },
  { icon: BookOpen, label: 'View Bookings', href: '/home-screen', color: 'text-gold' },
  { icon: MessageCircle, label: 'AI Assistant', href: '/ai-guide', color: 'text-sandalwood' },
];

export default function HomeHero() {
  const [searchValue, setSearchValue] = useState('');
  const [placeholder, setPlaceholder] = useState(aiSuggestions?.[0]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { user } = useAuth();

  const displayName = user?.user_metadata?.full_name?.split(' ')?.[0] || user?.email?.split('@')?.[0] || null;

  useEffect(() => {
    const interval = setInterval(() => {
      setSuggestionIndex((prev) => (prev + 1) % aiSuggestions?.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setPlaceholder(aiSuggestions?.[suggestionIndex]);
  }, [suggestionIndex]);

  const filteredSuggestions = aiSuggestions?.filter((s) =>
    s?.toLowerCase()?.includes(searchValue?.toLowerCase())
  );

  return (
    <section className="relative min-h-screen hero-gradient overflow-hidden flex flex-col">
      {/* Mandala Background Layers */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Large rotating mandala */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] opacity-10 animate-rotate-slow"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='200' cy='200' r='190' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='200' cy='200' r='160' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='130' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='100' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='70' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3Ccircle cx='200' cy='200' r='40' fill='none' stroke='%23D4AF37' stroke-width='0.8'/%3E%3C/svg%3E")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
          }}
        />
        {/* Warm glow orb */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-saffron/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-gold/15 blur-3xl" />
        {/* Floating particles */}
        {[...Array(8)]?.map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-gold/60 animate-float"
            style={{
              left: `${10 + i * 12}%`,
              top: `${15 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3 + (i % 3)}s`,
            }}
          />
        ))}
      </div>
      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 pt-24 pb-16">
        {/* Diya Animation */}
        <div className="mb-8 animate-float">
          <div className="relative w-24 h-24 mx-auto">
            {/* Glow ring */}
            <div className="absolute inset-0 rounded-full bg-saffron/30 blur-xl animate-glow-pulse" />
            <div className="relative w-24 h-24 flex items-center justify-center">
              <span className="text-6xl diya-glow" role="img" aria-label="Diya lamp">🪔</span>
            </div>
          </div>
        </div>

        {/* Personalised Greeting (shown when logged in) */}
        {displayName && (
          <div className="text-center mb-4 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/40 text-gold text-sm font-medium px-5 py-2 rounded-full tracking-wide">
              🙏 Namaste, <span className="font-bold">{displayName}</span>
            </div>
          </div>
        )}

        {/* Tagline */}
        <div className="text-center mb-4 animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-gold/20 border border-gold/40 text-gold text-xs font-medium px-4 py-1.5 rounded-full mb-4 tracking-wider uppercase">
            <Sparkles size={12} />
            AI-Powered Sacred Services
          </span>
        </div>

        {/* Headline */}
        <div className="text-center mb-6 animate-fade-in-up delay-100">
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-ivory leading-tight max-w-4xl mx-auto">
            Find the Right Priest
            <br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #FF9933, #D4AF37, #FF9933)', backgroundSize: '200% auto', animation: 'shimmer 3s linear infinite' }}>
              for Your Sacred Rituals
            </span>
          </h1>
          <p className="mt-4 text-ivory/70 text-lg md:text-xl max-w-2xl mx-auto font-body font-light leading-relaxed">
            Connect with verified, sampradaya-matched priests for authentic home poojas, life ceremonies, and daily rituals — across India.
          </p>
        </div>

        {/* Quick Actions (shown when logged in) */}
        {displayName && (
          <div className="flex flex-wrap justify-center gap-3 mb-6 animate-fade-in-up delay-100">
            {quickActions?.map((action) => (
              <Link
                key={action?.label}
                href={action?.href}
                className="flex items-center gap-2 glass-dark border border-gold/25 hover:border-gold/50 px-4 py-2.5 rounded-2xl transition-all duration-200 hover:bg-white/10 group"
              >
                <action.icon size={16} className={`${action?.color} group-hover:scale-110 transition-transform`} />
                <span className="text-ivory/80 text-sm font-body font-medium">{action?.label}</span>
              </Link>
            ))}
          </div>
        )}

        {/* AI Search Bar */}
        <div className="relative w-full max-w-2xl mx-auto animate-fade-in-up delay-200">
          <div className="relative glass-dark rounded-2xl p-1.5 shadow-2xl border border-gold/30 glow-gold">
            <div className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 text-gold flex-shrink-0">
                <Sparkles size={18} className="animate-glow-pulse" />
                <span className="text-xs font-medium text-gold/80 hidden sm:block">AI</span>
              </div>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e?.target?.value);
                  setShowSuggestions(e?.target?.value?.length > 0);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder={`e.g. "${placeholder}"`}
                className="flex-1 bg-transparent text-ivory placeholder-ivory/40 text-base outline-none font-body"
              />
              <Link
                href="/home-screen"
                className="flex-shrink-0 saffron-gradient text-white text-sm font-semibold px-5 py-2.5 rounded-xl btn-glow flex items-center gap-2"
              >
                <Search size={16} />
                <span className="hidden sm:block">Search</span>
              </Link>
            </div>
          </div>

          {/* AI Suggestions Dropdown */}
          {showSuggestions && filteredSuggestions?.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 glass-dark rounded-2xl border border-gold/30 overflow-hidden z-50 shadow-2xl animate-scale-in">
              {filteredSuggestions?.slice(0, 5)?.map((suggestion, i) => (
                <button
                  key={i}
                  onMouseDown={() => {
                    setSearchValue(suggestion);
                    setShowSuggestions(false);
                  }}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gold/10 transition-colors text-left border-b border-gold/10 last:border-0"
                >
                  <Sparkles size={14} className="text-gold flex-shrink-0" />
                  <span className="text-ivory/90 text-sm font-body">{suggestion}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick Tags */}
        <div className="flex flex-wrap justify-center gap-2 mt-5 animate-fade-in-up delay-300">
          {['Satyanarayan Pooja', 'Gruhapravesam', 'Navagraha Homa', 'Vivaha']?.map((tag) => (
            <button
              key={tag}
              className="text-xs bg-ivory/10 hover:bg-ivory/20 border border-ivory/20 hover:border-gold/40 text-ivory/80 hover:text-gold px-3 py-1.5 rounded-full transition-all duration-200 font-body"
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Trust Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-14 w-full max-w-3xl mx-auto animate-fade-in-up delay-400">
          {trustStats?.map((stat, i) => (
            <div
              key={i}
              className="glass-dark rounded-2xl px-4 py-4 text-center border border-gold/20"
            >
              <div className="font-display text-2xl font-bold text-gold tabular-nums">{stat?.value}</div>
              <div className="text-ivory/60 text-xs mt-1 font-body">{stat?.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="relative z-10 flex justify-center pb-6 animate-float">
        <ChevronDown size={24} className="text-ivory/40" />
      </div>
    </section>
  );
}